import moment from "moment";
import { memo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const Expired = styled.div<{ $backgroundColor?: string }>`
  display: flex;
  background: ${(props) => props.$backgroundColor ?? "#ae0010"};
  color: white;
  font-size: 0.8rem;
  margin-left: 10px;
  border-radius: 10px;
  align-items: center;
  padding: 0px 7px 3px;
`;

const Expiring = styled(Expired)`
  background: #e1821d;
`;

const FullText = styled.span`
  @media only screen and (max-width: 380px) {
    display: none;
  }
`;

const ShortText = styled.span`
  display: none;
  @media only screen and (max-width: 380px) {
    display: inline;
  }
`;

const ExpiresThresholdDays = 90;

interface ExpireBadgeProps {
  expirationDate?: Date;
}

const ExpireBadge = (props: ExpireBadgeProps) => {
  const { expirationDate } = props;
  const { t } = useTranslation("badge");

  let daysTillExpiration = undefined;

  if (!expirationDate) {
    return null;
  }

  const now = new Date();

  if (expirationDate) {
    daysTillExpiration = moment(expirationDate).diff(now, "days");
  }

  if (daysTillExpiration && daysTillExpiration < 0) {
    return (
      <Expired>
        <FullText>
          {t("badge.expiredDaysAgo", { count: -1 * daysTillExpiration })}
        </FullText>
        <ShortText>{t("badge.expired")}</ShortText>
      </Expired>
    );
  }

  if (
    daysTillExpiration &&
    daysTillExpiration < ExpiresThresholdDays &&
    daysTillExpiration > 0
  ) {
    return (
      <Expiring>
        <FullText>
          {t("badge.expiresInDays", { count: daysTillExpiration })}
        </FullText>
        <ShortText>{t("badge.expiring")}</ShortText>
      </Expiring>
    );
  }

  if (daysTillExpiration === 0 && expirationDate > now) {
    return (
      <Expiring>
        <FullText>{t("badge.expiresToday")}</FullText>
        <ShortText>{t("badge.expiring")}</ShortText>
      </Expiring>
    );
  }

  if (daysTillExpiration === 0 && expirationDate <= now) {
    return (
      <Expiring>
        <FullText>{t("badge.expiredToday")}</FullText>
        <ShortText>{t("badge.expired")}</ShortText>
      </Expiring>
    );
  }

  return null;
};

export default memo(ExpireBadge);
